/**
 * Reduces for the data service
 * @module services/data/reducers
 */
import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'
import omitBy from 'lodash/omitBy'
import { Map, List } from 'immutable'
import {
  DataStoreRecord,
  DataStoreViewRecord,
  findViewIndex,
  createViewRecordKeys,
  getDataStore,
  DATA_VIEWS_KEY,
  DATA_RECORDS_KEY,
  DATA_APPLICATION_KEY,
  DATA_VIEWS_RECORD_KEYS_KEY,
} from '..'
import {
  PERSIST_DATA,
  SET_DATA_LIST,
  GET_DATA_LIST,
  RESET_DATA,
  SET_DATA_APPLICATION,
  SET_DATA_APPLICATION_BY_KEY,
  DELETE_DATA_APPLICATION,
  DELETE_DATA_APPLICATION_BY_KEY,
} from './actions'

export const initialState = Map()

export default (state = initialState, action) => {
  const { payload = {}, type } = action
  switch (type) {
    case PERSIST_DATA:
    case GET_DATA_LIST:
    case SET_DATA_LIST: {
      const {
        storeKey, records, application, total, processing, error, options,
      } = payload
      const viewIndex = findViewIndex({ storeKey, data: state, options })
      const dataStore = getDataStore({ storeKey, data: state }) || DataStoreRecord()
      return state.set(storeKey, dataStore.withMutations((ds) => {
        if (records) {
          ds.mergeIn([DATA_RECORDS_KEY], records.toMap())
        }
        if (application) {
          ds.setIn([DATA_APPLICATION_KEY], application)
        }
        // If the view exist update the fields
        if (viewIndex > -1) {
          // The view exists so update the processing and error values
          // options does not change since that is how we matched the view
          ds.updateIn([DATA_VIEWS_KEY, viewIndex], (v) => v.withMutations((view) => {
            if (!isUndefined(processing)) {
              view.set('processing', processing)
            }
            if (isNull(error)) {
              view.delete('error')
            } else if (!isUndefined(error)) {
              view.set('error', error)
            }
            if (!isUndefined(records)) {
              view.set(DATA_VIEWS_RECORD_KEYS_KEY, createViewRecordKeys(records))
            }
            if (Number.isFinite(total)) {
              view.set('total', parseInt(total, 10))
            }
          }))
          return
        }
        // If we are here the view does not exist so create one
        const view = DataStoreViewRecord(omitBy({
          total: Number.isFinite(total) ? total : undefined,
          processing,
          error: isNull(error) ? undefined : error,
          options,
          [DATA_VIEWS_RECORD_KEYS_KEY]: records && createViewRecordKeys(records),
        }, isUndefined))
        ds.update(DATA_VIEWS_KEY, (views) => views.push(view))
      }))
    }
    case RESET_DATA: {
      const { storeKey } = payload
      return state.set(storeKey, DataStoreRecord())
    }
    case SET_DATA_APPLICATION: {
      const { storeKey, application } = payload
      const dataStore = getDataStore({ storeKey, data: state }) || DataStoreRecord()
      return state.set(storeKey, dataStore.set(DATA_APPLICATION_KEY, application))
    }
    case DELETE_DATA_APPLICATION: {
      const { storeKey } = payload
      const dataStore = getDataStore({ storeKey, data: state }) || DataStoreRecord()
      return state.set(storeKey, dataStore.delete(DATA_APPLICATION_KEY))
    }
    case SET_DATA_APPLICATION_BY_KEY: {
      const { storeKey, applicationData, applicationKey } = payload
      let dataStore = getDataStore({ storeKey, data: state }) || DataStoreRecord()
      dataStore = dataStore.withMutations((v) => {
        const applicationKeyList = List.isList(applicationKey)
          ? applicationKey
          : List([applicationKey])
        applicationKeyList.forEach((k) => v.setIn([DATA_APPLICATION_KEY, k], applicationData))
      })
      return state.set(storeKey, dataStore)
    }
    case DELETE_DATA_APPLICATION_BY_KEY: {
      const { storeKey, applicationData, applicationKey } = payload
      let dataStore = getDataStore({ storeKey, data: state }) || DataStoreRecord()
      dataStore = dataStore.withMutations((v) => {
        const applicationKeyList = List.isList(applicationKey)
          ? applicationKey
          : List([applicationKey])
        applicationKeyList.forEach((k) => v.deleteIn([DATA_APPLICATION_KEY, k], applicationData))
      })
      return state.set(storeKey, dataStore)
    }
    default:
      return state
  }
}
